import React from 'react';
import { Tip } from 'grommet';
import { SettingsOption } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { Box, Link, Text, UsagesDataTableCell, UsagesDataTableHeader } from '/src/components';
import { Equipment, EquipmentUsage } from '/src/lib/models';
import { DataTableCell, MeteredEquipmentUsageDataForm, UnmeteredEquipmentUsageDataForm } from '/src/lib/types';
import { getPeriodFromDateString } from '/src/utils';

export type UsagesDataTableProps = {
  equipmentUsages: EquipmentUsage[];
  tableCells: DataTableCell[];
  cellWidth?: string | number;
  equipment?: Equipment;
  formValues?: UnmeteredEquipmentUsageDataForm | MeteredEquipmentUsageDataForm;
  updateFormValue?: (key: string, value: string) => void;
  formErrors?: Record<string, string>;
  quarterColumnWidth?: number;
  showQuarterColumn?: boolean;
  isCanada?: boolean;
};

export const UsagesDataTable: React.FC<UsagesDataTableProps> = observer((props) => {
  const {
    equipmentUsages,
    tableCells,
    cellWidth,
    equipment,
    formValues,
    updateFormValue,
    formErrors,
    quarterColumnWidth = 8,
    showQuarterColumn = false,
    isCanada = false,
  } = props;

  const columnCount = tableCells.length || 1;
  const leftoverWidth = 100 - (showQuarterColumn ? (quarterColumnWidth ?? 0) : 0);
  const width =
    cellWidth ??
    (showQuarterColumn && quarterColumnWidth ? `${leftoverWidth / columnCount}%` : `${100 / columnCount}%`);

  const getRowBackground = (rowIndex: number) => (rowIndex % 2 ? 'white' : 'light-6');

  return (
    <Box border={{ side: 'all', size: '0.4px', color: 'light-2' }} row wrap fill="vertical">
      <Box direction="column" flex={false} height={'100%'}>
        <UsagesDataTableHeader label="" width="3rem" minWidth="3rem" />
        {equipmentUsages.map((equipmentUsage, rowIndex) => (
          <Box
            key={equipmentUsage.id ?? rowIndex}
            width="3rem"
            style={{ minWidth: '3rem' }}
            justify="center"
            align="center"
            border={{ side: 'all', size: '0.4px', color: 'light-2' }}
            pad={{ vertical: 'xsmall', horizontal: 'xsmall' }}
            background={getRowBackground(rowIndex)}
            height={'6rem'}
          >
            <Link to={`/clients/${equipment?.client_id}/equipment/${equipment?.id}/usages/${equipmentUsage.id}`}>
              <Box align="center" justify="center" style={{ height: '3rem', position: 'relative' }}>
                <Tip
                  content={`View ${getPeriodFromDateString(
                    equipmentUsage.start_reporting_quarter,
                    isCanada
                  )} Usage Details`}
                >
                  <SettingsOption size="20px" color="accent-1" />
                </Tip>
              </Box>
            </Link>
          </Box>
        ))}
      </Box>
      {showQuarterColumn && (
        <Box
          direction="column"
          flex={false}
          width={`${quarterColumnWidth}%`}
          height={'100%'}
          style={{ minWidth: '3rem' }}
        >
          <UsagesDataTableHeader label="Period" width={`${quarterColumnWidth}%`} />
          {equipmentUsages.map((equipmentUsage, rowIndex) => (
            <Box
              key={`${equipmentUsage.id ?? rowIndex}-period`}
              width="100%"
              align="center"
              border={{ side: 'all', size: '0.4px', color: 'light-2' }}
              pad={{ vertical: 'xsmall', horizontal: 'xsmall' }}
              background={getRowBackground(rowIndex)}
              style={{ height: '3rem' }}
            >
              <Text toUpperCase weight={500}>
                {getPeriodFromDateString(equipmentUsage.start_reporting_quarter, isCanada)}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {tableCells.map((cellData, columnIndex) => (
        <Box
          key={cellData.valueKey ?? cellData.label ?? columnIndex}
          direction="column"
          width={typeof width === 'number' ? width.toString() : width}
          flex={true}
          fill={true}
        >
          <UsagesDataTableHeader label={cellData.label} width={width} required={cellData.required ? true : undefined} />
          {equipmentUsages.map((equipmentUsage, rowIndex) => (
            <UsagesDataTableCell
              index={columnIndex}
              key={`${cellData.valueKey ?? columnIndex}-${equipmentUsage.id ?? rowIndex}`}
              width={typeof width === 'number' ? width.toString() : width}
              isEditable={cellData.isEditable}
              valueKey={cellData.valueKey}
              formValues={formValues}
              updateFormValue={updateFormValue}
              formErrors={formErrors}
              unit={cellData.unit}
              inputMode={cellData.isEditable ? cellData.inputMode : undefined}
              formatNumber={cellData.formatNumber}
              background={getRowBackground(rowIndex)}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
});
